import React from 'react';
import { styled, css } from 'styled-components';

import dayjs from '@/js/libs/dayjs/index.ts';

import { DatePickerRange } from './DatePickerRange.tsx';
import type { DateRangeType, DateType, RangePresets, ValidDateRange } from './types.ts';
import { isDefinedDateRange } from './types.ts';

const Wrapper = styled.div`
  display: flex;
  ${({ theme: { shadows } }) => shadows.large};

  .react-datepicker {
    border-radius: 0px;
    border: 0px;
    box-shadow: none !important;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.grey00_6};
`;

const Header = styled.div`
  padding-top: 18px;
  padding-bottom: 17px;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  width: 150px;
  text-align: center;
  border-right: 1px solid ${({ theme }) => theme.colors.grey10_1};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey10_1};
`;

const Switcher = styled.div<{ $isScrollable: boolean }>`
  padding: 10px;
  border-right: 1px solid ${({ theme }) => theme.colors.grey10_1};
  height: 100%;
  ${({ $isScrollable }) =>
    $isScrollable
      ? css`
          overflow: auto;
          max-height: 250px;
        `
      : ''}
`;

const SwitcherElement = styled.div<{ $isActive?: boolean; $isReadOnly?: boolean }>`
  padding: 6px 8px;
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
  margin-bottom: 4px;

  ${({ $isActive, theme }) =>
    $isActive
      ? css`
          background: ${theme.colors.main};
          border-radius: 2px;
          color: ${theme.colors.grey00_2};
        `
      : ''}

  ${({ $isActive, $isReadOnly, theme }) =>
    !$isActive && !$isReadOnly
      ? css`
          cursor: pointer;
          &:hover {
            background-color: ${theme.colors.grey05_1};
          }
        `
      : ''}
`;

const Footer = styled.div`
  padding: 12px 13px;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey10_1};
`;

interface RangePickerRangePresetsProps {
  rangePresets: RangePresets;
  onChange: (arg: ValidDateRange) => void;
  startDate: DateType;
  endDate: DateType;
  dateFormat?: string;
  periodTitle?: string;
}

const DatePickerRangePresets: React.FC<RangePickerRangePresetsProps> = ({
  rangePresets,
  onChange,
  dateFormat,
  startDate,
  endDate,
  periodTitle = 'Period',
}) => {
  const activeSwitcherRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const element = activeSwitcherRef.current;
    const parent = element?.parentElement;
    if (element && parent) {
      const parentBBox = parent.getBoundingClientRect();
      const elementBBox = element.getBoundingClientRect();
      if (elementBBox.top < parentBBox.top || elementBBox.bottom > parentBBox.bottom) {
        element.scrollIntoView();
      }
    }
  }, [startDate, endDate]);

  const handleSwitcherClick = (dateRange: DateRangeType) => {
    if (isDefinedDateRange(dateRange)) onChange(dateRange);
  };

  return (
    <Wrapper>
      <Column>
        <Header>{periodTitle}</Header>
        <Switcher $isScrollable={rangePresets.length > 8}>
          {rangePresets.map(({ isActive, title, dateRange, isReadOnly }) => {
            const isSwitcherActive = isActive([startDate, endDate], dateRange);
            return (
              <SwitcherElement
                key={title}
                $isActive={isSwitcherActive}
                $isReadOnly={isReadOnly}
                ref={isSwitcherActive ? activeSwitcherRef : undefined}
                onClick={isReadOnly ? undefined : () => handleSwitcherClick(dateRange)}>
                {title}
              </SwitcherElement>
            );
          })}
        </Switcher>
      </Column>
      <Column>
        <DatePickerRange startDate={startDate} endDate={endDate} onChange={onChange} dateFormat={dateFormat} />
        <Footer>
          {startDate ? dayjs(startDate).format('MMM DD, YYYY') : ''}
          {' - '}
          {endDate ? dayjs(endDate).format('MMM DD, YYYY') : ''}
        </Footer>
      </Column>
    </Wrapper>
  );
};

export default DatePickerRangePresets;
